import { type GraphQLResult } from '@aws-amplify/api-graphql'
import { API, graphqlOperation } from 'aws-amplify'
import React, { createContext, type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'

import * as collaborationsOperations from '../graphql/collaborations'
import { useGroup } from '../hooks/useGroup'
import { uniqueArray } from '../utils/array'
import {
  collaborationNextActionDisplay,
  getCollaborationNextAction,
  getCollaborationsFilteredByStatus,
  getFilteredCollaborations,
} from '../utils/collaboration'
import { type Collaboration, type CollaborationsContext } from './CollaborationProvider.types'

export const collaborationsContext = createContext<CollaborationsContext>({
  activeFilters: {
    nextAction: '',
    status: '',
  },
  clearFilters: undefined,
  collaborationNextActions: [],
  collaborationStates: [],
  collaborations: [],
  error: false,
  hasActiveFilters: false,
  loading: false,
  setActiveFilters: undefined,
})

interface CollaborationsProviderProps {
  children: ((context: CollaborationsContext) => ReactNode) | ReactNode
}

const initialFiltersState = {
  nextAction: '',
  status: '',
}

const CollaborationsProvider: FC<CollaborationsProviderProps> = ({ children }) => {
  const { group } = useGroup()
  const [activeFilters, setActiveFilters] = useState(initialFiltersState)

  /**
   * Retrieve collaborations, loading and error state.
   */
  const {
    isLoading: loading,
    isError: error,
    data: collaborations = [],
  } = useQuery(`getMyCollaborations-${group?.id}`, async () => {
    if (!group) return []

    const result = (await API.graphql(
      graphqlOperation(collaborationsOperations.getMyCollaborations, {
        groupId: group?.id,
      }),
    )) as GraphQLResult<{
      getMyCollaborations: Collaboration[]
    }>

    // Sort result descending by created date.
    return result.data?.getMyCollaborations.sort((pick, compare) =>
      pick.createdAt > compare.createdAt ? -1 : pick.createdAt < compare.createdAt ? 1 : 0,
    )
  })

  /**
   * Check if there is an active filter.
   */
  const hasActiveFilters = useMemo(
    (): boolean => Object.values(activeFilters).some((activeFilter) => activeFilter.length > 0),
    [activeFilters],
  )

  /**
   * Reset active filters to initial state.
   */
  const clearFilters = useCallback(() => {
    setActiveFilters({
      ...initialFiltersState,
    })
  }, [])

  /**
   * Collaborations filter by group id and active filters.
   */
  const filteredCollaborations = useMemo(
    () => getFilteredCollaborations(collaborations, activeFilters, group?.id),
    [collaborations, activeFilters, group],
  )

  /**
   * Convert collaboration states to user friendly labels.
   */
  const collaborationStates = useMemo(
    () => uniqueArray(collaborations.map((collaboration) => collaboration.state)),
    [collaborations],
  )

  /**
   * Collaboration next actions options.
   */
  const collaborationNextActions = useMemo((): {
    label: string
    value: string
  }[] => {
    const filteredCollaborations = getCollaborationsFilteredByStatus(collaborations, activeFilters.status)

    const nextActions = filteredCollaborations.map((collaboration) =>
      getCollaborationNextAction(collaboration, group?.id),
    )

    return uniqueArray(nextActions).map((nextAction) => ({
      label: collaborationNextActionDisplay[nextAction] as string,
      value: nextAction,
    }))
  }, [activeFilters, collaborations, group])

  /**
   * Update next action filter when there is exactly 1 next action available
   * and no current filter.
   */
  useEffect(() => {
    if (
      collaborationNextActions.length === 1 &&
      !activeFilters.nextAction &&
      activeFilters.nextAction !== collaborationNextActions[0].value
    ) {
      setActiveFilters({
        ...activeFilters,
        nextAction: collaborationNextActions[0].value,
      })
    }
  }, [collaborationNextActions, activeFilters])

  const context: CollaborationsContext = {
    activeFilters,
    clearFilters,
    collaborationNextActions,
    collaborationStates,
    collaborations: filteredCollaborations,
    error,
    hasActiveFilters,
    loading,
    setActiveFilters,
  }

  return (
    <collaborationsContext.Provider value={context}>
      {typeof children === 'function' ? children(context) : children}
    </collaborationsContext.Provider>
  )
}

export default CollaborationsProvider
